import { getInitData } from './telegram';
import type {
  CareDecisionDto,
  CareEventDto,
  CareTaskDto,
  DiagnosisDto,
  NotificationSettingsDto,
  PlantDto,
  SpaceDto,
  TodayDto,
  UserDto,
} from './types';

const BASE = import.meta.env.VITE_API_URL ?? '';

let token: string | null = null;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (token) headers.set('authorization', `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData)) headers.set('content-type', 'application/json');

  const response = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!response.ok) {
    let detail = `Ошибка ${response.status}`;
    try {
      const payload = await response.json();
      if (typeof payload?.detail === 'string') detail = payload.detail;
    } catch {
      // тело может быть пустым — оставляем общий текст
    }
    throw new ApiError(detail, response.status);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export interface AuthResponse {
  token: string;
  user: UserDto;
  spaces: SpaceDto[];
}

export const api = {
  async authenticate(): Promise<AuthResponse> {
    const result = await request<AuthResponse>('/api/v1/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ initData: getInitData() }),
      headers: { 'content-type': 'application/json' },
    });
    token = result.token;
    return result;
  },

  listSpaces: () => request<SpaceDto[]>('/api/v1/spaces'),

  today: (spaceId: string) =>
    request<TodayDto>(
      `/api/v1/spaces/${spaceId}/today?tz_offset_minutes=${new Date().getTimezoneOffset()}`,
    ),

  calendar: (spaceId: string, from: Date, to: Date, plantId?: string) =>
    request<CareTaskDto[]>(
      `/api/v1/spaces/${spaceId}/calendar?date_from=${encodeURIComponent(from.toISOString())}` +
        `&date_to=${encodeURIComponent(to.toISOString())}` +
        (plantId ? `&plant_id=${plantId}` : ''),
    ),

  tasks: (spaceId: string, days = 30) =>
    request<CareTaskDto[]>(`/api/v1/spaces/${spaceId}/tasks?days=${days}`),

  plants: (spaceId: string) => request<PlantDto[]>(`/api/v1/spaces/${spaceId}/plants`),

  plant: (plantId: string) => request<PlantDto>(`/api/v1/plants/${plantId}`),

  events: (plantId: string) => request<CareEventDto[]>(`/api/v1/plants/${plantId}/events`),

  decisions: (plantId: string) => request<CareDecisionDto[]>(`/api/v1/plants/${plantId}/care-decisions`),

  logEvent: (plantId: string, type: string) =>
    request<CareEventDto>(`/api/v1/plants/${plantId}/events`, {
      method: 'POST',
      body: JSON.stringify({ type }),
    }),

  completeTask: (taskId: string) =>
    request<CareTaskDto>(`/api/v1/tasks/${taskId}/complete`, { method: 'POST' }),

  snoozeTask: (taskId: string, hours = 24) =>
    request<CareTaskDto>(`/api/v1/tasks/${taskId}/snooze`, {
      method: 'POST',
      body: JSON.stringify({ hours }),
    }),

  soilStillWet: (taskId: string) =>
    request<CareTaskDto>(`/api/v1/tasks/${taskId}/soil-wet`, { method: 'POST' }),

  diagnoses: (spaceId: string) => request<DiagnosisDto[]>(`/api/v1/spaces/${spaceId}/diagnoses`),

  createDiagnosis: (spaceId: string, plantId: string | null, symptoms: string | null) =>
    request<DiagnosisDto>('/api/v1/diagnoses', {
      method: 'POST',
      body: JSON.stringify({ spaceId, plantId, userSymptoms: symptoms }),
    }),

  /** Фото растения для карточки и списка; новое становится главным. */
  uploadPlantPhoto: (plantId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{ id: string; url: string | null }>(`/api/v1/plants/${plantId}/photos?primary=true`, {
      method: 'POST',
      body: form,
    });
  },

  uploadDiagnosisImage: (diagnosisId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<DiagnosisDto>(`/api/v1/diagnoses/${diagnosisId}/images`, {
      method: 'POST',
      body: form,
    });
  },

  analyzeDiagnosis: (diagnosisId: string) =>
    request<DiagnosisDto>(`/api/v1/diagnoses/${diagnosisId}/analyze`, { method: 'POST' }),

  answerDiagnosis: (diagnosisId: string, questionKey: string, answer: string) =>
    request<DiagnosisDto>(`/api/v1/diagnoses/${diagnosisId}/answers`, {
      method: 'POST',
      body: JSON.stringify({ questionKey, answer }),
    }),

  diagnosisOutcome: (diagnosisId: string, outcome: 'better' | 'same' | 'worse') =>
    request<DiagnosisDto>(`/api/v1/diagnoses/${diagnosisId}/outcome`, {
      method: 'POST',
      body: JSON.stringify({ outcome }),
    }),

  createPlant: (spaceId: string, payload: Record<string, unknown>) =>
    request<PlantDto>(`/api/v1/spaces/${spaceId}/plants`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updatePlant: (plantId: string, payload: Record<string, unknown>) =>
    request<PlantDto>(`/api/v1/plants/${plantId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deletePlant: (plantId: string, reason: 'given_away' | 'died' | 'other' = 'other') =>
    request<void>(`/api/v1/plants/${plantId}?reason=${reason}`, { method: 'DELETE' }),

  settings: () => request<NotificationSettingsDto>('/api/v1/me/settings'),

  updateSettings: (payload: Partial<NotificationSettingsDto>) =>
    request<NotificationSettingsDto>('/api/v1/me/settings', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  weather: (spaceId: string) => request<unknown>(`/api/v1/spaces/${spaceId}/weather`),
};
